import { useState, useMemo, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao } from '../../componentes/base';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { Bloqueios } from './Bloqueios';
import { coresDaAgenda, corDaEscolha } from './cores';
import { CHAVE, useRecursos } from './dados';
import { HorarioEditor, resumoExpediente } from './HorarioEditor';
import { LembretesAgenda } from './Lembretes';
import { SalaReuniaoAgenda } from './Reuniao';
import { CORES_RECURSO, TIPOS_RECURSO, type CalendarioGoogle, type EstadoGoogle, type HorarioTrabalho, type Recurso } from './tipos';
import { nomePessoa } from '../../nucleo/formato';

const HORARIO_NOVO: HorarioTrabalho = Object.fromEntries(['1', '2', '3', '4', '5'].map((d) => [d, [{ de: '09:00', ate: '12:00' }, { de: '13:00', ate: '18:00' }]]));

/** Aba Profissionais e recursos: lista, editor de horário de trabalho, bloqueios por recurso e lembretes da Agenda. */
export function Recursos() {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Recurso | 'novo' | null>(null);
  const [horario, setHorario] = useState<HorarioTrabalho>(HORARIO_NOVO);
  const [arquivando, setArquivando] = useState(false);
  const [importar, setImportar] = useState(false);
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  const podeConfigurar = s.pode('agenda.configurar');

  // Fase 2 (W26): agenda do Google de cada item, só com o conector conectado e a permissão de configurar a Agenda.
  const google = useQuery({ queryKey: [CHAVE, 'google'], queryFn: () => get<EstadoGoogle>('/agenda/google'), enabled: podeConfigurar, staleTime: 30_000 });
  const comGoogle = podeConfigurar && !!google.data?.conectado;
  const calendarios = useQuery({
    queryKey: [CHAVE, 'google', 'calendarios'],
    queryFn: () => get<{ itens: CalendarioGoogle[] }>('/agenda/google/calendarios'),
    enabled: comGoogle && !!aberto,
    staleTime: 60_000,
    retry: false,
  });
  const agendaAtual = atual ? (google.data?.mapa[atual.id] ?? null) : null;
  const opcoesGoogle = [
    ...(calendarios.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.principal ? `${c.nome} (${t('google.principal')})` : c.nome })),
    // A agenda escolhida que sumiu da conta continua visível (para trocar ou deixar de sincronizar).
    ...(agendaAtual && calendarios.data && !calendarios.data.itens.some((c) => c.id === agendaAtual) ? [{ valor: agendaAtual, rotulo: `${agendaAtual} (${t('google.foraDaLista')})` }] : []),
  ];
  // Lista 30/10 B (item 34): ligado ao Google Agenda pessoal (Minha conta), a agenda daqui só vale se a pessoa desconectar.
  const pessoal = !!atual && !!google.data?.pessoais?.includes(atual.id);
  const ajudaGoogle = pessoal
    ? t('google.pessoal')
    : calendarios.isError
    ? mensagemDeErro(calendarios.error)
    : calendarios.isPending
      ? t('google.carregando')
      : opcoesGoogle.length === 0
        ? t('google.vazio')
        : t('google.ajuda');

  const abrir = (r: Recurso | 'novo') => {
    setHorario(r === 'novo' ? HORARIO_NOVO : r.horario_trabalho);
    setAberto(r);
  };

  const arquivar = useMutation({
    mutationFn: () => del(`/agenda/recursos/${atual!.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CHAVE] });
      setArquivando(false);
      setAberto(null);
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => (setArquivando(false), toast.erro(mensagemDeErro(e))),
  });

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, sensivel: atual && atual.tipo !== 'profissional' ? undefined : 'nome', desabilitado: !podeConfigurar },
    { nome: 'tipo', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: TIPOS_RECURSO.map((x) => ({ valor: x, rotulo: t(`tipos.${x}`) })), desabilitado: !podeConfigurar },
    { nome: 'cor', rotulo: t('campos.cor'), tipo: 'selecao', obrigatorio: true, largura: 'meia', ajuda: t('cores.ajuda'), opcoes: CORES_RECURSO.map((x) => ({ valor: x, rotulo: t(`cores.${x}`) })), desabilitado: !podeConfigurar },
    {
      nome: 'usuario_id',
      rotulo: t('campos.usuario'),
      tipo: 'busca',
      largura: 'meia',
      busca: { caminho: '/membros', rotulo: (i) => String(i.nome), pessoa: true },
      visivel: (v) => v.tipo === 'profissional',
    },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', largura: 'meia', desabilitado: !podeConfigurar },
    ...(comGoogle
      ? [
          {
            nome: 'calendar_id',
            rotulo: t('google.campo'),
            tipo: 'selecao',
            opcoes: opcoesGoogle,
            placeholder: t('google.naoSincronizar'),
            sensivel: 'email',
            desabilitado: !calendarios.isSuccess,
            ajuda: ajudaGoogle,
          } satisfies DefCampo,
        ]
      : []),
  ];

  const todosRecursos = useRecursos(false);
  const cores = useMemo(() => coresDaAgenda(todosRecursos.data ?? []), [todosRecursos.data]);
  const colunas: Coluna<Recurso>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), principal: true, ordenavel: true, render: (r) => <span className="font-medium">{r.tipo === 'profissional' ? nomePessoa(r.nome) : r.nome}</span> },
    { chave: 'tipo', rotulo: t('campos.tipo'), ordenavel: true, render: (r) => t(`tipos.${r.tipo}`) },
    {
      chave: 'cor',
      rotulo: t('campos.cor'),
      // rac-1 VIS-M3: a cor que a agenda usa de verdade, e não o token escolhido (Rosa saía vermelha; Azul, verde no Maré).
      render: (r) => {
        // rac-2 ROB2-B3: sem a lista (carregando ou com erro), o que está no próprio recurso, e não "Automática" para quem escolheu.
        const efetiva = cores.get(r.id) ?? (todosRecursos.data ? undefined : (r.cor_google ?? corDaEscolha(r.cor) ?? undefined));
        return (
          <span className="inline-flex items-center gap-1.5" data-cor-recurso>
            {efetiva && <span aria-hidden className="ponto-pessoa" style={{ '--cor-pessoa': efetiva } as CSSProperties} />}
            {r.cor_google ? t('cores.google') : efetiva && efetiva === corDaEscolha(r.cor) ? t(`cores.${r.cor}`) : t('cores.automatica')}
          </span>
        );
      },
    },
    { chave: 'expediente', rotulo: t('tabela.expediente'), render: (r) => resumoExpediente(r.horario_trabalho, t) },
    { chave: 'usuario_nome', rotulo: t('campos.usuario'), oculta: true, render: (r) => (r.usuario_nome ? nomePessoa(r.usuario_nome) : '') },
    { chave: 'ativo', rotulo: t('campos.ativo'), render: (r) => (r.ativo ? tn('comum.sim') : tn('comum.nao')) },
  ];

  return (
    <>
      {(podeConfigurar || s.pode('agenda.excluir')) && (
        <AcaoPrimaria>
          {/* Fase 2 (W25, K21): recurso arquivado (e bloqueio removido) voltam pela lixeira em até 30 dias. */}
          {s.pode('agenda.excluir') && <BotaoLixeira entidades={['agenda.recurso', 'agenda.bloqueio']} />}
          {podeConfigurar && (
            <>
              {/* Fase 2 (W18, K3): importador de profissionais e recursos (framework do núcleo, atualiza pelo nome). */}
              <BotaoModeloCsv caminho="/agenda/recursos" />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('acoes.importarRecursos')}
              </Botao>
              <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => abrir('novo')}>
                {t('acoes.novoRecurso')}
              </Botao>
            </>
          )}
        </AcaoPrimaria>
      )}
      <Tabela<Recurso> tela="agenda.recursos" caminho="/agenda/recursos" chaveConsulta={[CHAVE, 'tabela-recursos']} colunas={colunas} aoClicar={abrir} vazio={<Vazio texto={t('vazio.recursos')} />} />
      {/* Fase 2 (W18, K2): os lembretes moravam na aba Serviços da Agenda, que saiu (o catálogo é o de Cadastros > Serviços). */}
      <div className="mt-6">
        <LembretesAgenda />
      </div>
      {/* Lote 19 (12e): servidor de vídeo da "Sala da Malgo" (Jitsi auto-hospedado). */}
      <div className="mt-6">
        <SalaReuniaoAgenda />
      </div>
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? (atual.tipo === 'profissional' ? nomePessoa(atual.nome) : atual.nome) : t('acoes.novoRecurso')}
        rodape={
          <>
            {atual && s.pode('agenda.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setArquivando(true)}>
                {t('acoes.arquivar')}
              </Botao>
            )}
            {podeConfigurar && (
              <>
                <Botao variante="fantasma" onClick={() => setAberto(null)}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-recurso">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        <Formulario
          key={`${atual?.id ?? 'novo'}-${comGoogle}`}
          id="form-recurso"
          campos={campos}
          inicial={{ ...((atual ?? { tipo: 'profissional', cor: 'acento', ativo: true }) as unknown as Record<string, unknown>), calendar_id: agendaAtual }}
          rotulosAtuais={atual ? { usuario_id: atual.usuario_nome ?? '' } : undefined}
          aoEnviar={async (v) => {
            const corpo = {
              nome: v.nome,
              tipo: v.tipo,
              cor: v.cor,
              ativo: v.ativo !== false,
              usuario_id: v.tipo === 'profissional' ? ((v.usuario_id as string | null) ?? null) : null,
              horario_trabalho: Object.fromEntries(Object.entries(horario).filter(([, l]) => l.length > 0)),
            };
            let id = atual?.id;
            if (atual) await patch(`/agenda/recursos/${atual.id}`, corpo);
            else id = (await post<{ id: string }>('/agenda/recursos', corpo)).id;
            const agenda = comGoogle && calendarios.isSuccess ? ((v.calendar_id as string | null) || null) : agendaAtual;
            if (id && agenda !== agendaAtual) await api('PUT', `/agenda/recursos/${id}/google`, { calendar_id: agenda });
            await qc.invalidateQueries({ queryKey: [CHAVE] });
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        >
          <div className="sm:col-span-6">
            <HorarioEditor valor={horario} aoMudar={setHorario} />
          </div>
        </Formulario>
        {atual && (
          <div className="mt-6 border-t border-borda pt-4">
            <Bloqueios recursoId={atual.id} />
          </div>
        )}
      </Drawer>
      <Confirmar
        aberto={arquivando}
        aoFechar={() => setArquivando(false)}
        aoConfirmar={() => arquivar.mutate()}
        carregando={arquivar.isPending}
        perigoso
        titulo={t('confirmar.arquivarRecurso')}
        texto={t('confirmar.arquivarRecursoTexto')}
      />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho="/agenda/recursos" titulo={t('acoes.importarRecursos')} aoConcluir={() => void qc.invalidateQueries({ queryKey: [CHAVE] })} />
    </>
  );
}
